import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Divider,
  Typography,
  Table,
  InputNumber,
  App,
  Empty,
  Checkbox,
  Segmented,
} from 'antd';
import {
  FileTextOutlined,
  FilePdfOutlined,
  FileZipOutlined,
  SafetyCertificateOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import {
  ClienteFiscal,
  CreateClienteFiscalRequest,
  FacturaResult,
  ConceptoFactura,
  CrearFacturaSinOrdenRequest,
  FacturaDetalle,
} from '../../types/facturacion';
import {
  useGetClientesFiscalesQuery,
  useCreateClienteFiscalMutation,
  useCrearFacturaSinOrdenMutation,
  useRegenerarFacturaSinOrdenMutation,
  useTimbrarFacturaMutation,
  useCancelarFacturaMutation,
  useGetUsosCFDIQuery,
  useGetRegimenesFiscalesQuery,
  useGetFormasPagoQuery,
  useGetMetodosPagoQuery,
  useGetFacturacionConfigQuery,
  useSearchProductosServiciosQuery,
  useGetUnidadesMedidaQuery,
  useGetProductosFacturacionQuery,
} from '../../services/facturacionApi';
import { useGetProductsQuery, useGetCategoriesQuery } from '../../services/productsApi';
import { useGetStampBalanceQuery } from '../../services/stripeApi';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { getErrorMessage } from '../../utils/errorHelpers';
import { useAppSelector } from '../../hooks/redux';
import { canManageFacturas, isSupportUser } from '../../utils/roleHelpers';
import { useQRScanner } from '../../hooks/useQRScanner';
import QRScannerModal from './QRScannerModal';
import CancelarFacturaModal, { type CancelarFacturaFormValues } from './CancelarFacturaModal';
import './facturacion.css';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

/**
 * Normalizes SAT payment code to ensure it has leading zero if needed.
 */
const normalizeSATCode = (code: string): string => {
  if (code.length === 1 && /^\d$/.test(code)) {
    return '0' + code;
  }
  return code;
};

/**
 * Finds a matching catalog entry, handling the case where catalog has '4' but we need '04'.
 */
const findCatalogMatch = (
  catalogo: Array<{ clave: string }> | undefined,
  value: string
): string | null => {
  if (!catalogo) return null;

  const exactMatch = catalogo.find(item => item.clave === value);
  if (exactMatch) return exactMatch.clave;

  const withoutLeadingZero = value.replace(/^0/, '');
  const matchWithoutZero = catalogo.find(item => item.clave === withoutLeadingZero);
  if (matchWithoutZero) return matchWithoutZero.clave;

  if (value.length === 1 && /^\d$/.test(value)) {
    const withLeadingZero = '0' + value;
    const matchWithZero = catalogo.find(item => item.clave === withLeadingZero);
    if (matchWithZero) return matchWithZero.clave;
  }

  return null;
};

interface CrearFacturaModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: (factura: FacturaResult) => void;
  mode?: 'create' | 'regenerate';
  facturaToRegenerate?: FacturaDetalle;
}

const CrearFacturaModal: React.FC<CrearFacturaModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  mode = 'create',
  facturaToRegenerate,
}) => {
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCliente, setSelectedCliente] = useState<ClienteFiscal | null>(null);
  const [isNewCliente, setIsNewCliente] = useState(false);
  const [facturaResult, setFacturaResult] = useState<FacturaResult | null>(null);
  const [confirmandoCancelacion, setConfirmandoCancelacion] = useState(false);
  const [conceptos, setConceptos] = useState<ConceptoFactura[]>([]);
  const [searchClaveProdServ, setSearchClaveProdServ] = useState('');
  // Paso 2: buscador de productos + filtro de categoría + concepto libre inline
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  // QR de constancia fiscal para el alta de cliente nuevo
  const [showQRScanner, setShowQRScanner] = useState(false);
  const qrScanner = useQRScanner();

  // API hooks
  const { data: clientes, isLoading: clientesLoading } = useGetClientesFiscalesQuery();
  const { data: usosCFDI, isLoading: usosCFDILoading } = useGetUsosCFDIQuery();
  const { data: regimenesFiscales, isLoading: regimenesLoading } = useGetRegimenesFiscalesQuery();
  const { data: formasPago, isLoading: formasPagoLoading } = useGetFormasPagoQuery();
  const { data: metodosPago, isLoading: metodosPagoLoading } = useGetMetodosPagoQuery();
  const { data: facturacionConfig } = useGetFacturacionConfigQuery();
  const { data: unidadesMedida } = useGetUnidadesMedidaQuery();
  const { data: productosServicios, isFetching: searchingClaves } = useSearchProductosServiciosQuery(
    searchClaveProdServ,
    { skip: searchClaveProdServ.length < 2 }
  );
  const { data: products } = useGetProductsQuery({ availableOnly: false });
  const { data: categories } = useGetCategoriesQuery({ activeOnly: true });
  const { data: productosFacturacion } = useGetProductosFacturacionQuery();
  const { data: stampBalance } = useGetStampBalanceQuery();

  const [createCliente, { isLoading: creatingCliente }] = useCreateClienteFiscalMutation();
  const [crearFactura, { isLoading: creandoFactura }] = useCrearFacturaSinOrdenMutation();
  const [regenerarFacturaSinOrden, { isLoading: regenerandoFactura }] = useRegenerarFacturaSinOrdenMutation();
  const [timbrarFactura, { isLoading: timbrandoFactura }] = useTimbrarFacturaMutation();
  const [cancelarFactura, { isLoading: cancelandoFactura }] = useCancelarFacturaMutation();
  const { user } = useAppSelector((state) => state.auth);
  // Cancelar ante el SAT es solo de administración (el backend lo exige; esto evita el 403)
  const puedeCancelar = canManageFacturas(user?.role, isSupportUser(user?.email));

  // Categorías con productos (para los chips de filtro)
  const categoriesWithProducts = useMemo(() => {
    if (!products || !categories) return [];
    return categories.filter(cat => products.some(p => p.categoryId === cat.id));
  }, [products, categories]);

  // Lista filtrada por búsqueda y categoría
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    const search = productSearch.trim().toLowerCase();
    return products.filter(p => {
      if (selectedCategoryId && p.categoryId !== selectedCategoryId) return false;
      if (search && !p.name.toLowerCase().includes(search)) return false;
      return true;
    });
  }, [products, productSearch, selectedCategoryId]);

  // Watch del checkbox para reactividad en totales
  const desglosarIVAWatch = Form.useWatch('desglosarIVA', form);

  // Calculate totals
  // Nota: Los precios ingresados YA INCLUYEN IVA
  // Excepto para cliente S01 (Sin efectos fiscales) donde NO se aplican impuestos
  const totales = useMemo(() => {
    // Verificar si el cliente tiene UsoCFDI S01 (sin efectos fiscales)
    // Respetar checkbox de desglose IVA cuando es S01
    const esSinEfectosFiscales = (selectedCliente?.usoCFDI === 'S01' ||
                                  form.getFieldValue('usoCFDI') === 'S01') &&
                                  !desglosarIVAWatch;

    // Total = suma de precios ingresados (ya incluyen IVA)
    const total = conceptos.reduce((sum, c) => {
      const importe = c.cantidad * c.precioUnitario - c.descuento;
      return sum + importe;
    }, 0);

    if (esSinEfectosFiscales) {
      // Cliente público en general / sin efectos fiscales: NO hay impuestos
      return { subtotal: total, iva: 0, total, aplicaImpuestos: false };
    } else {
      // Cliente regular: Desglosar IVA (precios YA incluyen IVA)
      // Subtotal = Total / 1.16
      const subtotal = total / 1.16;
      const iva = total - subtotal;
      return { subtotal, iva, total, aplicaImpuestos: true };
    }
  }, [conceptos, selectedCliente, form, desglosarIVAWatch]);

  useEffect(() => {
    if (visible) {
      setCurrentStep(0);
      setSelectedCliente(null);
      setIsNewCliente(false);
      setFacturaResult(null);
      setConceptos([]);
      setProductSearch('');
      setSelectedCategoryId(null);
      setShowQRScanner(false);
      form.resetFields();

      // Modo regenerar - precargar datos de la factura existente
      if (mode === 'regenerate' && facturaToRegenerate && clientes) {
        // Buscar cliente fiscal por RFC
        const cliente = clientes.find(c => c.rfc === facturaToRegenerate.clienteRFC);
        if (cliente) {
          setSelectedCliente(cliente);
          form.setFieldsValue({
            clienteId: cliente.id,
            formaPago: facturaToRegenerate.formaPago,
            metodoPago: facturaToRegenerate.metodoPago,
          });
        }

        // Precargar conceptos de la factura
        if (facturaToRegenerate.conceptos && facturaToRegenerate.conceptos.length > 0) {
          const conceptosPreloaded: ConceptoFactura[] = facturaToRegenerate.conceptos.map((c, index) => ({
            key: `concept-${index}-${Date.now()}`,
            tipo: 'libre',
            claveProdServ: c.claveProdServ,
            claveUnidad: c.claveUnidad,
            unidad: c.unidad || 'Pieza',
            descripcion: c.descripcion,
            cantidad: c.cantidad,
            precioUnitario: c.precioUnitario,
            descuento: c.descuento,
            observaciones: c.observaciones,
          }));
          setConceptos(conceptosPreloaded);
        }
      } else {
        // Modo crear - usar defaults
        const formaPago = findCatalogMatch(formasPago, facturacionConfig?.formaPagoDefault || '01') || '01';
        const metodoPago = facturacionConfig?.metodoPagoDefault || 'PUE';
        form.setFieldsValue({ formaPago, metodoPago });
      }
    }
  }, [visible, form, facturacionConfig, formasPago, mode, facturaToRegenerate, clientes]);

  // Toggle Existente / Nuevo del paso 1
  const handleClienteModeChange = (mode: 'existente' | 'nuevo') => {
    if (mode === 'nuevo') {
      setIsNewCliente(true);
      setSelectedCliente(null);
      form.setFieldsValue({
        clienteId: undefined,
        rfc: undefined,
        razonSocial: undefined,
        nombreComercial: undefined,
        usoCFDI: facturacionConfig?.usoCFDIDefault || undefined,
        regimenFiscal: undefined,
        email: undefined,
        telefono: undefined,
        codigoPostal: undefined,
        calle: undefined,
        numeroExterior: undefined,
        numeroInterior: undefined,
      });
    } else {
      setIsNewCliente(false);
      setSelectedCliente(null);
      form.setFieldsValue({ clienteId: undefined });
    }
  };

  const handleClienteSelect = (clienteId: string) => {
    // Find client by facturadorClienteId (not by id, as id is Guid.Empty for all)
    const cliente = clientes?.find((c) => c.facturadorClienteId === Number(clienteId));
    setSelectedCliente(cliente || null);
    // Set form fields with selected client data
    if (cliente) {
      form.setFieldsValue({
        rfc: cliente.rfc,
        razonSocial: cliente.razonSocial,
        nombreComercial: cliente.nombreComercial,
        usoCFDI: cliente.usoCFDI,
        regimenFiscal: cliente.regimenFiscal,
        email: cliente.email,
        telefono: cliente.telefono,
      });
    }
  };

  // QR de constancia fiscal: prellenar el alta de cliente nuevo
  const handleQRDetected = async (qrContent: string) => {
    setShowQRScanner(false);
    const parsedData = await qrScanner.handleScan(qrContent);
    if (parsedData) {
      form.setFieldsValue({
        rfc: parsedData.rfc,
        razonSocial: parsedData.razonSocial,
        regimenFiscal: parsedData.regimenFiscal,
        codigoPostal: parsedData.codigoPostal,
      });
      message.success('Datos de la constancia cargados. Completa los campos restantes.');
    } else if (qrScanner.error) {
      message.error(qrScanner.error, 5);
    }
    qrScanner.resetScanner();
  };

  const handleAddProducto = (productId: string) => {
    const product = products?.find(p => p.id === productId);
    if (!product) return;

    // Check if product already exists in conceptos
    const existingIndex = conceptos.findIndex(c => c.productId === productId);
    if (existingIndex >= 0) {
      // Increment quantity
      const updated = [...conceptos];
      updated[existingIndex].cantidad += 1;
      setConceptos(updated);
      return;
    }

    // Get SAT mapping if exists
    const mapeo = productosFacturacion?.find(m => m.productId === productId);

    const newConcepto: ConceptoFactura = {
      key: `prod-${Date.now()}`,
      tipo: 'producto',
      productId: product.id,
      productName: product.name,
      claveProdServ: mapeo?.claveProdServ || '90101501', // 90101501 = Servicios de restaurante
      claveUnidad: mapeo?.claveUnidad || 'Q3', // Q3 = Comida
      unidad: 'Pieza',
      descripcion: product.name,
      cantidad: 1,
      precioUnitario: product.price,
      descuento: 0,
    };

    setConceptos([...conceptos, newConcepto]);
    message.success(`${product.name} agregado`);
  };

  // Agrega un renglón libre vacío directo a la tabla; ahí se edita todo inline.
  const handleAddConceptoLibre = () => {
    const newConcepto: ConceptoFactura = {
      key: `libre-${Date.now()}`,
      tipo: 'libre',
      claveProdServ: '90101501', // Servicios de restaurante
      claveUnidad: 'Q3',         // Comida
      unidad: 'Comida',
      descripcion: '',
      cantidad: 1,
      precioUnitario: 0,
      descuento: 0,
    };
    setConceptos(prev => [...prev, newConcepto]);
  };

  const handleRemoveConcepto = (key: string) => {
    setConceptos(prev => prev.filter(c => c.key !== key));
  };

  const handleUpdateConcepto = (key: string, campos: Partial<ConceptoFactura>) => {
    setConceptos(prev => prev.map(c => {
      if (c.key !== key) return c;
      const updated = { ...c, ...campos };
      // El descuento no puede exceder el importe de la línea (saldría negativo).
      const maxDescuento = updated.cantidad * updated.precioUnitario;
      if (updated.descuento > maxDescuento) updated.descuento = maxDescuento;
      return updated;
    }));
  };

  const handleStep1Next = async () => {
    try {
      // Validate all required fields for new client.
      // codigoPostal incluido: CFDI 4.0 exige el DomicilioFiscalReceptor para timbrar.
      if (isNewCliente) {
        await form.validateFields(['rfc', 'razonSocial', 'usoCFDI', 'regimenFiscal', 'codigoPostal', 'email']);
      }
      setCurrentStep(1);
    } catch (err) {
      console.error('Step 1 validation failed:', err);
    }
  };

  const handleStep2Next = () => {
    if (conceptos.length === 0) {
      message.warning('Agregue al menos un concepto');
      return;
    }

    // Validate that all productos have SAT mapping
    const sinMapeo = conceptos.filter(c => c.tipo === 'producto' && !c.claveProdServ);
    if (sinMapeo.length > 0) {
      message.error(`Los siguientes productos no tienen mapeo SAT: ${sinMapeo.map(c => c.productName).join(', ')}`);
      return;
    }

    // Los conceptos libres se editan inline; se validan aquí al avanzar
    if (conceptos.some(c => c.tipo === 'libre' && !c.descripcion.trim())) {
      message.error('Hay conceptos libres sin descripción');
      return;
    }
    if (conceptos.some(c => c.tipo === 'libre' && c.descripcion.length > 1000)) {
      message.error('Hay descripciones de más de 1000 caracteres (límite del SAT)');
      return;
    }

    setCurrentStep(2);
  };

  const handleCrearFactura = async () => {
    try {
      // Get ALL form values (including fields not currently rendered)
      const values = form.getFieldsValue(true);

      let clienteData: ClienteFiscal;

      if (isNewCliente) {
        const newClienteRequest: CreateClienteFiscalRequest = {
          rfc: values.rfc,
          razonSocial: values.razonSocial,
          nombreComercial: values.nombreComercial,
          usoCFDI: values.usoCFDI,
          regimenFiscal: values.regimenFiscal,
          email: values.email,
          telefono: values.telefono,
          codigoPostal: values.codigoPostal,
          calle: values.calle,
          numeroExterior: values.numeroExterior,
          numeroInterior: values.numeroInterior,
        };

        const createdCliente = await createCliente(newClienteRequest).unwrap();
        clienteData = createdCliente;
      } else if (selectedCliente) {
        clienteData = selectedCliente;
      } else {
        message.error('Debe seleccionar o crear un cliente fiscal');
        return;
      }

      const request: CrearFacturaSinOrdenRequest = {
        clienteFiscal: clienteData,
        formaPago: normalizeSATCode(values.formaPago || '01'),
        metodoPago: values.metodoPago || 'PUE',
        conceptos: conceptos.map(c => ({
          tipo: c.tipo,
          productId: c.productId,
          claveProdServ: c.claveProdServ,
          claveUnidad: c.claveUnidad,
          unidad: c.unidad,
          descripcion: c.descripcion,
          cantidad: c.cantidad,
          precioUnitario: c.precioUnitario,
          descuento: c.descuento,
          observaciones: c.observaciones,
        })),
        notas: values.notas,
        desglosarIVA: values.desglosarIVA || false,
      };

      let result: FacturaResult;

      if (mode === 'regenerate' && facturaToRegenerate) {
        // Modo regenerar - llamar endpoint de regeneración
        result = await regenerarFacturaSinOrden({
          facturaId: facturaToRegenerate.facturaId,
          request,
        }).unwrap();
        message.success('Factura regenerada exitosamente');
      } else {
        // Modo crear - flujo normal
        result = await crearFactura(request).unwrap();
        message.success('Factura generada exitosamente');
      }

      setFacturaResult(result);
      setCurrentStep(3);

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (error: unknown) {
      console.error('Error al crear factura:', error);
      const errorMsg = getErrorMessage(error, 'Error al generar la factura');
      message.error(errorMsg, 8);
    }
  };

  const handleDownloadPDF = () => {
    if (facturaResult) {
      const pdfUrl = `${window.location.origin}/api${facturaResult.pdfUrl}`;
      window.open(pdfUrl, '_blank');
    }
  };

  const handleDownloadXML = () => {
    if (facturaResult && facturaResult.xmlUrl) {
      const xmlUrl = `${window.location.origin}/api${facturaResult.xmlUrl}`;
      window.open(xmlUrl, '_blank');
    }
  };

  const handleTimbrarFactura = async () => {
    if (!facturaResult) return;

    try {
      const result = await timbrarFactura(facturaResult.facturaId).unwrap();

      setFacturaResult({
        ...facturaResult,
        uuid: result.uuid,
        fechaTimbrado: result.fechaTimbrado,
        rfcProvCertif: result.rfcProvCertif,
        xmlUrl: result.xmlUrl,
        estado: result.estado,
      });

      message.success('Factura timbrada exitosamente');
    } catch (error: unknown) {
      console.error('Error al timbrar factura:', error);
      const errorMsg = getErrorMessage(error, 'Error al timbrar la factura');
      message.error(errorMsg, 8);
    }
  };

  const handleCancelarFactura = async () => {
    if (!facturaResult || !facturaResult.uuid) {
      message.warning('Solo se pueden cancelar facturas timbradas');
      return;
    }

    // El motivo se pregunta: iba fijo en '02' sin consultar al usuario, y el UUID de
    // sustitucion --que el motivo 01 exige-- nunca se llenaba. Mismo arreglo que el
    // PR #146 hizo en FacturasManager y SolicitarFacturaModal.
    setConfirmandoCancelacion(true);
  };

  const confirmarCancelacion = async (values: CancelarFacturaFormValues) => {
    if (!facturaResult) return;

    try {
      const resultado = await cancelarFactura({
        facturaId: facturaResult.facturaId,
        data: { motivo: values.motivo, uuidSustitucion: values.uuidSustitucion },
      }).unwrap();

      // "Solicitada" no es "cancelada": si el receptor debe aceptarla, el SAT tarda.
      if (resultado.confirmada) message.success(resultado.mensaje);
      else message.info(resultado.mensaje, 8);
      setFacturaResult({ ...facturaResult, estado: resultado.confirmada ? 'Cancelada' : 'CancelacionEnProceso' });
      setConfirmandoCancelacion(false);
    } catch (error: unknown) {
      const errorMsg = getErrorMessage(error, 'Error al cancelar la factura');
      message.error(errorMsg, 8);
    }
  };

  const conceptosColumns = [
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (value: string, record: ConceptoFactura) =>
        record.tipo === 'libre' ? (
          <>
            {/* Sin maxLength: recortaría lo pegado en silencio; se valida al avanzar */}
            <TextArea
              value={value}
              placeholder="Descripción del concepto"
              autoSize={{ minRows: 1, maxRows: 4 }}
              size="small"
              status={value.length > 1000 ? 'error' : undefined}
              onChange={(e) => handleUpdateConcepto(record.key, { descripcion: e.target.value })}
            />
            {value.length > 1000 && (
              <Text type="danger" style={{ fontSize: 11 }}>
                Máximo 1000 caracteres (límite del SAT) · {value.length}
              </Text>
            )}
          </>
        ) : (
          value
        ),
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 100,
      render: (value: number, record: ConceptoFactura) => (
        <InputNumber
          min={0.01}
          step={1}
          value={value}
          onChange={(v) => handleUpdateConcepto(record.key, { cantidad: v || 1 })}
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Precio',
      dataIndex: 'precioUnitario',
      key: 'precioUnitario',
      width: 120,
      render: (value: number, record: ConceptoFactura) => (
        <InputNumber
          min={0}
          step={1}
          value={value}
          onChange={(v) => handleUpdateConcepto(record.key, { precioUnitario: v || 0 })}
          prefix="$"
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Descuento',
      dataIndex: 'descuento',
      key: 'descuento',
      width: 110,
      render: (value: number, record: ConceptoFactura) => (
        <InputNumber
          min={0}
          max={record.cantidad * record.precioUnitario}
          step={1}
          value={value}
          onChange={(v) => handleUpdateConcepto(record.key, { descuento: v || 0 })}
          prefix="$"
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Importe',
      key: 'importe',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, record: ConceptoFactura) => (
        <span className="fac-money">${(record.cantidad * record.precioUnitario - record.descuento).toFixed(2)}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, record: ConceptoFactura) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveConcepto(record.key)}
          size="small"
        />
      ),
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: {
        const usoDesc = usosCFDI?.find(u => u.clave === selectedCliente?.usoCFDI)?.descripcion;
        const regimenDesc = regimenesFiscales?.find(r => r.clave === selectedCliente?.regimenFiscal)?.descripcion;

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
              <Segmented
                value={isNewCliente ? 'nuevo' : 'existente'}
                onChange={(value) => handleClienteModeChange(value as 'existente' | 'nuevo')}
                options={[
                  { label: 'Cliente existente', value: 'existente' },
                  { label: 'Nuevo cliente', value: 'nuevo' },
                ]}
              />
              {isNewCliente && (
                <Button icon={<ScanOutlined />} onClick={() => setShowQRScanner(true)}>
                  Escanear constancia
                </Button>
              )}
            </div>

            {!isNewCliente ? (
              <>
                <Form.Item
                  label="Cliente"
                  name="clienteId"
                  rules={[{ required: true, message: 'Seleccione un cliente' }]}
                >
                  <Select
                    placeholder="Busca por RFC o razón social"
                    onChange={handleClienteSelect}
                    loading={clientesLoading}
                    showSearch
                    optionFilterProp="children"
                  >
                    {clientes?.map((cliente) => (
                      <Option key={cliente.facturadorClienteId} value={cliente.facturadorClienteId}>
                        {cliente.rfc} · {cliente.razonSocial}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Cliente elegido: resumen de solo lectura, no un formulario deshabilitado */}
                {selectedCliente && (
                  <div className="fac-summary">
                    <div className="fac-summary__item">
                      <span className="fac-summary__label">Uso CFDI</span>
                      {selectedCliente.usoCFDI}{usoDesc ? ` · ${usoDesc}` : ''}
                    </div>
                    <div className="fac-summary__item">
                      <span className="fac-summary__label">Régimen</span>
                      {selectedCliente.regimenFiscal}{regimenDesc ? ` · ${regimenDesc}` : ''}
                    </div>
                    <div className="fac-summary__item">
                      <span className="fac-summary__label">Email</span>
                      {selectedCliente.email || '—'}
                    </div>
                    {selectedCliente.codigoPostal && (
                      <div className="fac-summary__item">
                        <span className="fac-summary__label">C.P. fiscal</span>
                        {selectedCliente.codigoPostal}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <Row gutter={8}>
                  <Col xs={24} sm={9}>
                    <Form.Item
                      label="RFC"
                      name="rfc"
                      hasFeedback
                      normalize={(value) => value?.toUpperCase()}
                      rules={[
                        { required: true, message: 'RFC requerido' },
                        {
                          pattern: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/,
                          message: 'RFC inválido (12-13 caracteres)'
                        },
                      ]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input
                        placeholder="XAXX010101000"
                        maxLength={13}
                        style={{ fontFamily: 'ui-monospace, Consolas, monospace' }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={15}>
                    <Form.Item
                      label="Razón social"
                      name="razonSocial"
                      rules={[{ required: true, message: 'Razón social requerida' }]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input placeholder="Nombre o razón social" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={8}>
                  <Col xs={24} sm={9}>
                    <Form.Item
                      label="Uso CFDI"
                      name="usoCFDI"
                      rules={[{ required: true, message: 'Uso de CFDI requerido' }]}
                      style={{ marginBottom: 8 }}
                    >
                      <Select
                        placeholder="Seleccione uso de CFDI"
                        loading={usosCFDILoading}
                        showSearch
                        optionFilterProp="children"
                      >
                        {usosCFDI?.map((uso) => (
                          <Option key={uso.clave} value={uso.clave}>
                            {uso.clave} - {uso.descripcion}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={9}>
                    <Form.Item
                      label="Régimen fiscal"
                      name="regimenFiscal"
                      rules={[{ required: true, message: 'Régimen fiscal requerido' }]}
                      style={{ marginBottom: 8 }}
                    >
                      <Select
                        placeholder="Seleccione régimen fiscal"
                        loading={regimenesLoading}
                        showSearch
                        optionFilterProp="children"
                      >
                        {regimenesFiscales?.map((regimen) => (
                          <Option key={regimen.clave} value={regimen.clave}>
                            {regimen.clave} - {regimen.descripcion}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={6}>
                    {/* CFDI 4.0 exige el DomicilioFiscalReceptor (C.P.) para timbrar */}
                    <Form.Item
                      label="C.P. fiscal"
                      name="codigoPostal"
                      rules={[
                        { required: true, message: 'C.P. requerido' },
                        { pattern: /^\d{5}$/, message: '5 dígitos' },
                      ]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input placeholder="01000" maxLength={5} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={8}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Email"
                      name="email"
                      rules={[
                        { required: true, message: 'Email requerido' },
                        { type: 'email', message: 'Email inválido' },
                      ]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input placeholder="correo@ejemplo.com" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Teléfono"
                      name="telefono"
                      style={{ marginBottom: 8 }}
                    >
                      <Input placeholder="5512345678" />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </div>
        );
      }

      case 1:
        return (
          <div>
            {/* Buscador + chips de categoría + concepto libre como toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <Input
                placeholder="Buscar producto del catálogo…"
                prefix={<SearchOutlined />}
                allowClear
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                style={{ flex: 1, minWidth: 200 }}
              />
              <div className="fac-pills">
                <button
                  type="button"
                  className={`fac-pill${!selectedCategoryId ? ' fac-pill--selected' : ''}`}
                  onClick={() => setSelectedCategoryId(null)}
                >
                  Todas
                </button>
                {categoriesWithProducts.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`fac-pill${selectedCategoryId === cat.id ? ' fac-pill--selected' : ''}`}
                    onClick={() => setSelectedCategoryId(selectedCategoryId === cat.id ? null : cat.id)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 4 }}>
              {filteredProducts.length === 0 ? (
                <Empty
                  description={productSearch ? 'Sin resultados' : 'No hay productos disponibles'}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                filteredProducts.map(product => {
                  const hasMapeo = productosFacturacion?.some(m => m.productId === product.id);
                  const categoryName = categories?.find(c => c.id === product.categoryId)?.name;
                  return (
                    <div key={product.id} className="fac-prodrow">
                      <span>{product.name}</span>
                      {categoryName && <span className="fac-prodrow__cat">{categoryName}</span>}
                      {/* Sin mapeo se dice con palabras, no con un borde de color sin leyenda */}
                      {!hasMapeo && <span className="fac-warnpill">sin mapeo SAT</span>}
                      <div style={{ flex: 1 }} />
                      <span className="fac-money">${product.price.toFixed(2)}</span>
                      <Button
                        type="text"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => handleAddProducto(product.id)}
                      />
                    </div>
                  );
                })
              )}
            </div>

            <Divider style={{ margin: '8px 0' }}>Conceptos · {conceptos.length}</Divider>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Button size="small" icon={<EditOutlined />} onClick={handleAddConceptoLibre}>
                Concepto libre
              </Button>
            </div>

            <Table
              dataSource={conceptos}
              columns={conceptosColumns}
              rowKey="key"
              size="small"
              pagination={false}
              locale={{ emptyText: 'No hay conceptos agregados' }}
              scroll={{ x: 640 }}
              expandable={{
                rowExpandable: (record) => record.tipo === 'libre',
                expandedRowRender: (record) => (
                  <Row gutter={8}>
                    <Col xs={24} sm={12}>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                        Clave Prod/Serv SAT
                      </div>
                      <Select
                        showSearch
                        size="small"
                        style={{ width: '100%' }}
                        value={record.claveProdServ}
                        placeholder="Buscar clave SAT..."
                        filterOption={false}
                        onSearch={setSearchClaveProdServ}
                        loading={searchingClaves}
                        notFoundContent={searchClaveProdServ.length < 2 ? 'Escriba para buscar' : 'Sin resultados'}
                        onChange={(v) => handleUpdateConcepto(record.key, { claveProdServ: v })}
                      >
                        <Option key="90101501" value="90101501">
                          90101501 - Servicios de restaurante
                        </Option>
                        {record.claveProdServ !== '90101501' &&
                          !productosServicios?.some(ps => ps.clave === record.claveProdServ) && (
                            <Option key={record.claveProdServ} value={record.claveProdServ}>
                              {record.claveProdServ}
                            </Option>
                          )}
                        {productosServicios?.filter(ps => ps.clave !== '90101501').map(ps => (
                          <Option key={ps.clave} value={ps.clave}>
                            {ps.clave} - {ps.descripcion}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                        Clave Unidad SAT
                      </div>
                      <Select
                        showSearch
                        size="small"
                        style={{ width: '100%' }}
                        optionFilterProp="children"
                        value={record.claveUnidad}
                        onChange={(v) => {
                          const um = unidadesMedida?.find(u => u.clave === v);
                          handleUpdateConcepto(record.key, {
                            claveUnidad: v,
                            unidad: v === 'Q3' ? 'Comida' : um?.nombre || 'Pieza',
                          });
                        }}
                      >
                        <Option key="Q3" value="Q3">
                          Q3 - Comida
                        </Option>
                        {unidadesMedida?.filter(um => um.clave !== 'Q3').map(um => (
                          <Option key={um.clave} value={um.clave}>
                            {um.clave} - {um.nombre}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                ),
              }}
            />

            <div style={{ display: 'flex', marginTop: 10 }}>
              <div className="fac-totales">
                <div className="fac-totales__row">
                  <span>Subtotal</span>
                  <span className="fac-money">${totales.subtotal.toFixed(2)}</span>
                </div>
                {totales.aplicaImpuestos && (
                  <div className="fac-totales__row">
                    <span>IVA 16%</span>
                    <span className="fac-money">${totales.iva.toFixed(2)}</span>
                  </div>
                )}
                <div className="fac-totales__row fac-totales__row--total">
                  <span>Total</span>
                  <span className="fac-money">${totales.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            {/* Resumen como panel neutro: es contexto, no una alerta */}
            <div className="fac-summary" style={{ marginBottom: 18 }}>
              <div className="fac-summary__item">
                <span className="fac-summary__label">Cliente</span>
                {selectedCliente?.razonSocial || form.getFieldValue('razonSocial')}{' '}
                <span className="fac-mono" style={{ color: 'var(--text-secondary)' }}>
                  {selectedCliente?.rfc || form.getFieldValue('rfc')}
                </span>
              </div>
              <div className="fac-summary__item">
                <span className="fac-summary__label">Conceptos</span>
                {conceptos.length}
              </div>
              <div style={{ flex: 1 }} />
              <div className="fac-summary__item" style={{ textAlign: 'right' }}>
                <span className="fac-summary__label">Total</span>
                <span className="fac-money" style={{ fontSize: 17, color: 'var(--color-primary)' }}>
                  ${totales.total.toFixed(2)}
                </span>
              </div>
            </div>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Forma de Pago"
                  name="formaPago"
                  rules={[{ required: true, message: 'Forma de pago requerida' }]}
                >
                  <Select
                    placeholder="Seleccione forma de pago"
                    loading={formasPagoLoading}
                    showSearch
                    optionFilterProp="children"
                  >
                    {formasPago?.map((forma) => (
                      <Option key={forma.clave} value={forma.clave}>
                        {forma.clave} - {forma.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Método de Pago"
                  name="metodoPago"
                  rules={[{ required: true, message: 'Método de pago requerido' }]}
                >
                  <Select
                    placeholder="Seleccione método de pago"
                    loading={metodosPagoLoading}
                    showSearch
                    optionFilterProp="children"
                  >
                    {metodosPago?.map((metodo) => (
                      <Option key={metodo.clave} value={metodo.clave}>
                        {metodo.clave} - {metodo.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {(selectedCliente?.usoCFDI === 'S01' || form.getFieldValue('usoCFDI') === 'S01') && (
              <Form.Item
                name="desglosarIVA"
                valuePropName="checked"
                initialValue={false}
                style={{ marginBottom: 16 }}
              >
                <Checkbox>
                  Desglosar IVA (el uso de CFDI S01 no desglosa IVA por defecto)
                </Checkbox>
              </Form.Item>
            )}

            <Form.Item label="Notas (opcional)" name="notas">
              <TextArea rows={2} placeholder="Notas internas sobre esta factura..." />
            </Form.Item>
          </div>
        );

      case 3: {
        if (!facturaResult) return null;

        const estadoCls =
          facturaResult.estado === 'Timbrada' ? ' fac-estado--timbrada' :
          facturaResult.estado === 'Cancelada' ? ' fac-estado--cancelada' : '';
        const timbresDespues = stampBalance ? stampBalance.totalDisponibles - 1 : null;

        return (
          <div>
            {/* Cierre operativo: folio + estado + total, sin ceremonia */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              flexWrap: 'wrap',
              border: '1px solid var(--border-color)',
              borderRadius: 10,
              padding: '14px 16px',
            }}>
              <div>
                <div style={{ fontSize: 19, fontWeight: 650 }}>
                  {facturaResult.serie}-{facturaResult.folio}{' '}
                  <span className={`fac-estado${estadoCls}`} style={{ verticalAlign: 2 }}>
                    {facturaResult.estado}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {selectedCliente?.razonSocial || form.getFieldValue('razonSocial')} · {conceptos.length} concepto{conceptos.length !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ flex: 1 }} />
              <span className="fac-money" style={{ fontSize: 20 }}>${facturaResult.total.toFixed(2)}</span>
            </div>

            {facturaResult.uuid && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
                <span className="fac-mono">UUID: </span>
                <Text copyable className="fac-mono" style={{ fontSize: 12 }}>{facturaResult.uuid}</Text>
                {facturaResult.fechaTimbrado && (
                  <span> · timbrada el {formatInTimeZone(facturaResult.fechaTimbrado, 'DD/MM/YYYY HH:mm')}</span>
                )}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              {!facturaResult.uuid && facturaResult.estado !== 'Cancelada' && (
                <Button
                  type="primary"
                  icon={<SafetyCertificateOutlined />}
                  onClick={handleTimbrarFactura}
                  loading={timbrandoFactura}
                  block={isMobile}
                >
                  Timbrar ahora
                  {timbresDespues !== null && ` (1 timbre · quedan ${Math.max(timbresDespues, 0)})`}
                </Button>
              )}
              <Button icon={<FilePdfOutlined />} onClick={handleDownloadPDF} block={isMobile}>
                PDF
              </Button>
              {facturaResult.xmlUrl && (
                <Button icon={<FileZipOutlined />} onClick={handleDownloadXML} block={isMobile}>
                  XML
                </Button>
              )}
              {puedeCancelar && facturaResult.uuid && facturaResult.estado !== 'Cancelada' && facturaResult.estado !== 'CancelacionEnProceso' && (
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handleCancelarFactura}
                  loading={cancelandoFactura}
                  block={isMobile}
                >
                  Cancelar factura
                </Button>
              )}
              {!isMobile && <div style={{ flex: 1 }} />}
              {!facturaResult.uuid && facturaResult.estado === 'Borrador' && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  puedes timbrarla después desde la lista
                </span>
              )}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  const renderFooter = () => {
    const isLoading = creatingCliente || creandoFactura || regenerandoFactura;

    if (currentStep === 3) {
      return [
        <Button key="close" type="primary" onClick={onCancel}>
          Cerrar
        </Button>,
      ];
    }

    return [
      <Button key="cancel" onClick={onCancel}>
        Cancelar
      </Button>,
      currentStep > 0 && currentStep < 3 && (
        <Button key="back" onClick={() => setCurrentStep(currentStep - 1)}>
          Anterior
        </Button>
      ),
      currentStep === 0 && (
        <Button
          key="next"
          type="primary"
          onClick={handleStep1Next}
          disabled={!selectedCliente && !isNewCliente}
        >
          Siguiente
        </Button>
      ),
      currentStep === 1 && (
        <Button
          key="next2"
          type="primary"
          onClick={handleStep2Next}
          disabled={conceptos.length === 0}
        >
          Siguiente
        </Button>
      ),
      currentStep === 2 && (
        <Button
          key="submit"
          type="primary"
          onClick={handleCrearFactura}
          loading={isLoading}
          icon={<FileTextOutlined />}
        >
          {mode === 'regenerate' ? 'Regenerar Factura' : 'Crear Factura'}
        </Button>
      ),
    ];
  };

  const STEP_TITLES = ['Cliente', 'Conceptos', 'Pago', 'Listo'];

  return (
    <Modal
      title={mode === 'regenerate' ? 'Regenerar factura' : 'Nueva factura'}
      open={visible}
      onCancel={onCancel}
      footer={renderFooter()}
      width={isMobile ? '100%' : 900}
      destroyOnClose
    >
      <div className="fac-wsteps">
        {STEP_TITLES.map((title, i) => (
          <React.Fragment key={title}>
            {i > 0 && <span className="fac-wsteps__sep" />}
            <span
              className={`fac-wstep${
                i < currentStep ? ' fac-wstep--done' : i === currentStep ? ' fac-wstep--current' : ''
              }`}
            >
              <span className="fac-wstep__n">{i < currentStep ? '✓' : i + 1}</span>
              {title}
            </span>
          </React.Fragment>
        ))}
      </div>

      <Form form={form} layout="vertical">
        {renderStepContent()}
      </Form>

      {/* Escáner de constancia fiscal para el alta de cliente nuevo */}
      <QRScannerModal
        visible={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onQRDetected={handleQRDetected}
        onManualEntry={() => setShowQRScanner(false)}
      />

      <CancelarFacturaModal
        open={confirmandoCancelacion}
        folio={facturaResult ? `${facturaResult.serie}-${facturaResult.folio}` : undefined}
        // Sin esto el boton sigue habilitado durante la mutacion y cada clic manda otra
        // cancelacion al SAT.
        confirmLoading={cancelandoFactura}
        onCancel={() => setConfirmandoCancelacion(false)}
        onConfirm={confirmarCancelacion}
      />
    </Modal>
  );
};

export default CrearFacturaModal;
